import { IResourceComponentsProps, useList, useNavigation, usePermissions, useShow } from "@pankod/refine-core";

import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import listPlugin from '@fullcalendar/list';
import { IRentPlace, RentPlaceStatusEnum } from "../../interfaces/rent-place.interface";
import { Button, Typography, useModal, List } from "@pankod/refine-antd";
import { useEffect, useRef, useState } from "react";
import { useLocation } from "@pankod/refine-react-router-v6";
import { rentPlaceStatusOptions, statusColors, placeColors, getAllowedPlaces } from "../../constants/rentPlace.constants";
import { TableOutlined } from "@ant-design/icons";
import { RentPlaceDetailModal } from "../../components/molecules/rent-place-detail-modal";

const { Title, Text } = Typography;

// Hook personalizado para detectar móviles
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isMobile;
};

export const RentPlaceCalendarPage: React.FC<IResourceComponentsProps> = () => {

  const { create, push } = useNavigation();
  const { data: permissionsData } = usePermissions();
  const isMobile = useIsMobile();

  const location: any = useLocation();
  const calendarRef = useRef<FullCalendar | null>(null);

  const [initialView, setInitialView] = useState<string>("dayGridMonth");

  const { data, isLoading, refetch } = useList<IRentPlace>({
    resource: "rent-places",
    config: {
      hasPagination: false,
      filters: [
        {
          field: "hide_canceled",
          operator: "eq",
          value: true
        }
      ]
    }
  });

  const { modalProps: showModalProps, show, close } = useModal();
  const { queryResult, showId, setShowId } = useShow<IRentPlace>({
    resource: "rent-places"
  });
  const { data: showData } = queryResult;
  const showRecord = showData?.data;

  const rentPlaceEvents = data?.data.map((rentPlace: any) => {
    let start = rentPlace.start_date;
    let end = rentPlace.end_date;
    
    // Si hay tiempos específicos, los agregamos a las fechas
    if (rentPlace.start_time) {
      start = `${rentPlace.start_date}T${rentPlace.start_time}`;
    }
    
    if (rentPlace.end_date && rentPlace.end_time) {
      end = `${rentPlace.end_date}T${rentPlace.end_time}`;
    } else if (rentPlace.end_date) {
      // Si solo hay fecha final sin hora, agregamos un día para mostrar correctamente
      end = new Date(new Date(rentPlace.end_date).getTime() + 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    }

    return {
      id: rentPlace.id,
      title: rentPlace.event_name || `Evento ${rentPlace.id}`,
      start, 
      end,
      extendedProps: {
        place: rentPlace.place,
        status: rentPlace.status,
        event_name: rentPlace.event_name,
        responsible_name: rentPlace.responsible_name,
        responsible_phone: rentPlace.responsible_phone,
        observations: rentPlace.observations,
        is_free: rentPlace.is_free,
        start_time: rentPlace.start_time,
        end_time: rentPlace.end_time,
        institution: rentPlace.institution,
      },
      backgroundColor: placeColors[rentPlace.place] || statusColors[rentPlace.status] || '#1890ff',
      borderColor: placeColors[rentPlace.place] || '#1890ff',
    };
  }) || [];

  useEffect(() => {
    if (location.state?.defaultCalendarView) {
      setInitialView(location.state?.defaultCalendarView);
    }
  }, [location.state]);

  useEffect(() => {
    if (location.state?.defaultCalendarView && calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      calendarApi.changeView(location.state?.defaultCalendarView);
    }
  }, [location.state]);

  useEffect(() => {
    const handleResize = () => {
      if (calendarRef.current) {
        const calendarApi = calendarRef.current.getApi();
        calendarApi.updateSize();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleEventClick = (info: any) => {
    setShowId(info.event.id);
    show();
  };

  const handleCloseModal = () => {
    close();
    refetch();
  };

  const renderEventContent = (eventInfo: any) => {
    const { extendedProps } = eventInfo.event;

    // Mejorar el formato de tiempo para mostrar rangos completos
    let timeDisplay = "";
    if (extendedProps.start_time && extendedProps.end_time) {
      timeDisplay = `${extendedProps.start_time} - ${extendedProps.end_time}`;
    } else if (extendedProps.start_time) {
      timeDisplay = extendedProps.start_time;
    } else if (eventInfo.timeText) {
      const normalizedTimeText = eventInfo.timeText.includes(":") 
        ? eventInfo.timeText 
        : `${eventInfo.timeText}:00`;
      timeDisplay = normalizedTimeText;
    }

    // Icono según el lugar
    const placeIcon = extendedProps.place === "POLIDEPORTIVO" ? "🏃" : 
                     extendedProps.place === "TEATRO" ? "🎭" : "📍";

    // Vista diferente según el tipo de calendario
    const isTimeGridView = eventInfo.view.type.includes('timeGrid');
    
    if (isTimeGridView) {
      // Vista compacta para vistas de tiempo (semana/día)
      return (
        <div className="calendar-custom-event">
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '2px' }}>
            <span style={{ fontSize: '14px' }}>{placeIcon}</span>
            <strong style={{ fontSize: '12px' }}>{timeDisplay}</strong>
            <span 
              style={{
                backgroundColor: statusColors[extendedProps.status] || '#1890ff',
                color: 'white',
                fontSize: '8px',
                padding: '1px 4px',
                borderRadius: '2px',
                fontWeight: 'bold',
                marginLeft: '4px'
              }}
            >
              {rentPlaceStatusOptions[extendedProps.status as RentPlaceStatusEnum]?.substring(0, 3) || extendedProps.status?.substring(0, 3)}
            </span>
          </div>
          <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#333' }}>
            {extendedProps.is_free ? "🆓 " : extendedProps.status === "PAID" ? "✅💰 " : "💰 "}{eventInfo.event.title}
          </div>
          <div style={{ fontSize: '9px', color: '#666' }}>
            {extendedProps.institution}
          </div>
        </div>
      );
    }

    // Vista completa para vista de mes
    return (
      <div className="calendar-custom-event">
        <div className="calendar-event-details">
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span style={{ fontSize: '14px' }}>{placeIcon}</span>
            <b style={{ fontSize: '11px' }}>{timeDisplay}</b>
          </div>
          <span 
            className="calendar-event-status"
            style={{
              backgroundColor: statusColors[extendedProps.status] || '#1890ff',
              color: 'white',
              fontWeight: 'bold'
            }}
          >
            {rentPlaceStatusOptions[extendedProps.status as RentPlaceStatusEnum] || extendedProps.status}
          </span>
        </div>
        <div className="calendar-event-details">
          <span style={{ fontSize: '10px', fontWeight: 'bold' }}>
            {extendedProps.place}
          </span>
        </div>
        <i className="calendar-event-title">
          {extendedProps.is_free ? "🆓 " : extendedProps.status === "PAID" ? "✅💰 " : "💰 "}{eventInfo.event.title}
        </i>
        {extendedProps.responsible_name && (
          <div className="calendar-event-budget">
            👤 {extendedProps.institution}
          </div>
        )}
      </div>
    );
  }

  const isAdmin = permissionsData?.includes('rent-places:admin');
  const allowedPlaces = getAllowedPlaces(permissionsData || []);
  const hasAnyPlacePermission = allowedPlaces.length > 0;

  const customButtons = hasAnyPlacePermission ? {
    newRentPlace: {
      text: 'Nueva Renta',
      click: function() {
        create('rent-places');
      }
    }
  } : undefined;

  return (
    <List
      title="Calendario de Renta de Lugares"
      headerButtons={({ defaultButtons }) => ([
        ...(Array.isArray(defaultButtons) ? defaultButtons : [defaultButtons]),
        <Button 
          key="listView" 
          type="default" 
          icon={<TableOutlined />}
          onClick={() => push('/rent-places', { state: { fromCalendar: true } })}
        >
          Vista Lista
        </Button>
      ])}
      contentProps={{
        style: { 
          height: 'calc(100vh - 64px)', 
          overflow: 'hidden',
          padding: 0 
        }
      }}
    >
      {isLoading ? (
        <p>Cargando...</p>
      ) : (
        <div className="rent-places-calendar-wrapper" style={{ height: '100%' }}>
          <FullCalendar
            ref={calendarRef}
            plugins={[dayGridPlugin, timeGridPlugin, listPlugin]}
            initialView={initialView}
            weekends={true}
            events={rentPlaceEvents}
            eventContent={renderEventContent}
            eventClick={handleEventClick}
            locale={'es'}
            customButtons={customButtons}
            height="100%"
            aspectRatio={isMobile ? 1.0 : 1.35}
            slotMinTime="06:00:00"
            slotMaxTime="24:00:00"
            slotDuration="01:00:00"
            slotLabelInterval="01:00:00"
            slotLabelFormat={{
              hour: '2-digit',
              minute: '2-digit',
              hour12: false
            }}
            eventDisplay="block"
            displayEventTime={true}
            eventTimeFormat={{
              hour: '2-digit',
              minute: '2-digit',
              hour12: false
            }}
            headerToolbar={{
              left: hasAnyPlacePermission ? 'prev,next today' : 'prev,next today',
              center: 'title',
              right: isMobile ? 'dayGridMonth listWeek listMonth' : 'dayGridMonth,timeGridWeek,timeGridDay listWeek,listMonth'
            }}
            buttonText={{
              today: 'Hoy',
              month: 'Mes',
              week: 'Semana',
              day: 'Día',
              listWeek: 'Lista Semanal',
              listMonth: 'Lista Mensual',
            }}
            themeSystem="bootstrap5"
          />
        </div>
      )}

      <RentPlaceDetailModal
        visible={!!showModalProps.visible}
        onCancel={handleCloseModal}
        onOk={handleCloseModal}
        isLoading={isLoading}
        showId={showId || ""}
        showRecord={showRecord}
        hasEditPermission={hasAnyPlacePermission}
      />
    </List>
  );
};
